/** SYSTEMATIC PLANS — Transactions, Client SIP tab. */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { MF_SIPS, MF_SWPS, MF_STPS } from '../queries';
import { inr, inrPlain } from '../../lib/format';
import { EMPTY, sum } from './_shared';

export function useSystematicPlans() {
  const sips = useQuery(MF_SIPS, { variables: { status: null }, fetchPolicy: 'cache-first' });
  const swps = useQuery(MF_SWPS, { variables: { status: null }, fetchPolicy: 'cache-first' });
  const stps = useQuery(MF_STPS, { variables: { status: null }, fetchPolicy: 'cache-first' });

  const sipRows = sips.data?.mfXsips ?? EMPTY;
  return {
    loading: sips.loading || swps.loading || stps.loading,
    error: sips.error || swps.error || stps.error,
    sips: sipRows,
    swps: swps.data?.mfSwps ?? EMPTY,
    stps: stps.data?.mfStps ?? EMPTY,
    monthlySip: sum(
      sipRows.filter((s) => /ACTIVE|APPROVED/i.test(s.xsipStatus || '')),
      (s) => s.installmentAmount,
    ),
    failing: sipRows.filter((s) => (Number(s.consecutiveFailures) || 0) > 0),
  };
}

/**
 * All three plan kinds merged into one dated timeline, newest first —
 * the Transactions screen's row model. `filter`: all | sip | stp | swp | failed.
 */
export function usePlanTimeline(plans, filter = 'all') {
  return useMemo(() => {
    const sips = plans.sips.map((s) => ({
      kind: 'sip',
      id: `sip-${s.id}`,
      date: s.sipStartDate || s.createdAt,
      type: 'SIP instalment',
      detail: `${s.installmentsCompleted ?? 0} of ${s.noOfInstallments ?? '∞'} instalments${s.stepUpAmount ? ` · step-up ${inrPlain(s.stepUpAmount)}` : ''}`,
      scheme: s.schemeName || s.schemeCode,
      amount: s.installmentAmount,
      mode: s.frequency || 'NACH',
      status: Number(s.consecutiveFailures) > 0 ? `${s.consecutiveFailures} failed debits` : s.xsipStatus,
      ref: s.platformSipRegNo,
      attention: Number(s.consecutiveFailures) > 0,
    }));
    const stps = plans.stps.map((s) => ({
      kind: 'stp',
      id: `stp-${s.id}`,
      date: s.stpStartDate || s.createdAt,
      type: 'STP transfer',
      detail: `${s.route || 'NATIVE'} route · ${s.installmentsCompleted ?? 0} of ${s.noOfInstallments ?? '∞'}${s.inTransitAmount ? ` · ${inr(s.inTransitAmount)} in transit` : ''}`,
      scheme: `${s.sourceSchemeName} → ${s.targetSchemeName}`,
      amount: s.transferAmount,
      mode: s.frequency || '—',
      status: s.unifiedStatus || s.stpStatus,
      ref: s.platformStpRegNo,
      attention: /ATTENTION/i.test(s.unifiedStatus || ''),
      reason: s.attentionReason,
    }));
    const swps = plans.swps.map((s) => ({
      kind: 'swp',
      id: `swp-${s.id}`,
      date: s.swpStartDate || s.createdAt,
      type: 'SWP payout',
      detail: `${s.installmentsCompleted ?? 0} of ${s.noOfInstallments ?? '∞'} withdrawals`,
      scheme: s.schemeName || s.schemeCode,
      amount: s.withdrawalAmount,
      mode: s.frequency || '—',
      status: s.swpStatus,
      ref: s.platformSwpRegNo,
      attention: false,
    }));
    const all = [...sips, ...stps, ...swps].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    if (filter === 'all') return all;
    if (filter === 'failed') return all.filter((r) => r.attention);
    return all.filter((r) => r.kind === filter);
  }, [plans, filter]);
}
